import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  ChangeEvent
} from 'react';
import { useParams, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Box
} from '@material-ui/core';
import {
  AddRounded as AddIcon,
  PeopleRounded as ImportIcon,
  ChevronRightRounded as GoToUserIcon,
  ArrowBackRounded as BackIcon,
  DeleteRounded as DeleteIcon,
  CloudUploadRounded as UploadIcon,
  SearchRounded as SearchIcon
} from '@material-ui/icons';
import styled from 'styled-components';

import {
  AuthContext,
  buildFileUploadEndpoint,
  isOrgReadOnly,
  useDebounce,
  useFeatureFlags,
  useSpot
} from 'framework';
import { Alert } from '@material-ui/lab';
import {
  ConfirmDialog,
  Line,
  Row,
  Spacer,
  Gap,
  Table,
  TitleContainer,
  UserResult,
  EditUserDialog,
  ImportUsersDialog
} from 'components';
import { OnboardingRequiredDocumentType } from 'domain-types';
import { StaffTabs } from './staff-tabs';
import { UserInfo } from './user-info';

const SearchBox = styled(Box)`
  flex: 1 1 auto;
`;

interface EditUserDialogDesc {
  callback: (user: UserResult) => Promise<void>;
  requiredOnboardingDocuments?: OnboardingRequiredDocumentType[];
  firstName?: string;
  lastName?: string;
  dob?: string;
  email?: string;
  startDate?: string;
  managerId?: string;
  title?: string;
}

interface ConfirmDialogDesc {
  title: string;
  text: string;
  callback: () => Promise<void>;
  cancel?: () => unknown;
}

interface ImportUsersDialog {
  callback: () => Promise<void>;
}

function FileUploadButton({
  organization,
  id,
  disabled = false
}: {
  organization: string;
  id: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { getToken } = useContext(AuthContext);
  const fileInput = useRef<HTMLInputElement>(null);
  const { command } = useSpot();

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.files?.length && uploadFile(e.target.files[0]);
  };

  const uploadFile = async (file: File) => {
    if (!file || disabled) {
      return;
    }

    // Fetch upload url
    const uploadResponse = await fetch(
      buildFileUploadEndpoint(file.name, organization, id),
      {
        headers: {
          authorization: getToken().getJwtToken()
        },
        method: 'get'
      }
    );

    const { url } = await uploadResponse.json();

    // Upload
    await fetch(url, {
      method: 'put',
      body: file
    });

    await command(`org/${organization}/user/${id}/notify`, {
      type: 'new-file',
      information: {
        filename: file.name
      }
    });
  };

  return (
    <IconButton
      onClick={() => fileInput.current?.click()}
      title={t('uploadFile')}
      disabled={disabled}
    >
      <UploadIcon />
      <input
        ref={fileInput}
        type="file"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />
    </IconButton>
  );
}

export function Users() {
  const { t } = useTranslation();
  const history = useHistory();
  const { isAdministrator } = useContext(AuthContext);
  const { organization, userId, tab } = useParams<{
    organization: string;
    userId?: string;
    tab?: string;
  }>();
  const [editDialog, setEditDialog] = useState<EditUserDialogDesc | null>(null);
  const [
    importUsersDialog,
    setImportUsersDialog
  ] = useState<ImportUsersDialog | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogDesc | null>(
    null
  );
  const canAdministerUsers = isAdministrator();

  const { global } = useFeatureFlags();

  const { data, errors, query, command } = useSpot();
  const org = data?.organizations?.[organization];
  const readOnly = isOrgReadOnly(org);

  const searchUsers = async (st: string) => {
    setLoading(true);
    await query(`org/${organization}/search`, { searchTerm: st }, [
      'searchUsers'
    ]);
    setLoading(false);
  };

  const debouncedSearch = useDebounce(searchUsers, 500);

  useEffect(() => {
    if (errors.find(err => err.status === 401)) {
      window.location.reload();
    }
  }, [errors]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [organization, query, searchTerm, debouncedSearch, userId]);

  useEffect(() => {
    if (!userId || !canAdministerUsers) {
      return;
    }

    query(`org/${organization}/user/${userId}`, {}, ['users', userId]);
  }, [canAdministerUsers, organization, query, userId]);

  const onAdd = () => {
    if (readOnly) {
      return;
    }

    const showAddDialog = () =>
      setEditDialog({
        requiredOnboardingDocuments: org?.onboardingRequiredDocuments,
        callback: async userResult => {
          const response = (await command(`org/${organization}/user`, {
            ...userResult
          })) as { id?: string };
          debouncedSearch(searchTerm);
          if (response?.id) {
            await query(`org/${organization}/user/${response.id}`, {}, [
              'users',
              response.id
            ]);
            history.push(`/${organization}/users/${response.id}/onboarding`);
          }
          setEditDialog(null);
        }
      });

    const currentUserCount = Object.values(data?.searchUsers || {}).length;
    if (currentUserCount === 14 || currentUserCount === 49) {
      setConfirmDialog({
        title: t('confirmPriceChangeTitle'),
        text: t('confirmPriceChangeSubtitle', {
          tier: t(currentUserCount === 14 ? 'startup' : 'large'),
          price: t(currentUserCount === 14 ? 'startupPrice' : 'largePrice')
        }),
        callback: async () => showAddDialog()
      });
    } else {
      showAddDialog();
    }
  };

  const onDelete = (id: string) => {
    if (readOnly) {
      return;
    }

    setConfirmDialog({
      title: t('confirmUserDeleteTitle'),
      text: t('confirmUserDeleteSubtitle'),
      callback: async () => {
        await command(
          `org/${organization}/user/${id}`,
          {},
          { method: 'DELETE' }
        );
        await debouncedSearch(searchTerm);
        setConfirmDialog(null);
      }
    });
  };

  const onImport = () => {
    if (readOnly) {
      return;
    }

    setImportUsersDialog({
      callback: async () => {
        await command(
          `org/${organization}/createUsers`,
          {},
          { method: 'POST' }
        );
        await debouncedSearch(searchTerm);
        setConfirmDialog(null);
      }
    });
  };

  const filteredUsers = (data?.searchUsers
    ? Object.values(data?.searchUsers)
    : []
  ).sort((userA, userB) => userA.firstName.localeCompare(userB.firstName));

  const selectUser = (id: string) => {
    history.push(`/${organization}/users/${id}`);
  };

  const onTabChange = (selectedTab: string) => {
    if (!userId) {
      return;
    }

    history.replace(`/${organization}/users/${userId}/${selectedTab}`);
  };

  const deselectUser = () => {
    history.push(`/${organization}/users`);
  };
  const selectedUser = userId
    ? data?.users?.[userId] ?? data?.searchUsers?.[userId]
    : undefined;

  return (
    <StaffTabs organization={organization} selected="users">
      {userId ? (
        <>
          <TitleContainer>
            <Typography variant="h5">
              <IconButton onClick={() => deselectUser()} title={t('back')}>
                <BackIcon />
              </IconButton>
              {`${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ??
                ''}`}
            </Typography>
          </TitleContainer>
          <Line />

          <UserInfo
            id={userId}
            organization={organization}
            tab={tab}
            onTabChange={onTabChange}
            disabledTabs={
              canAdministerUsers
                ? undefined
                : [
                    'profile',
                    'applications',
                    'files',
                    'leaveConfig',
                    'hourConfig'
                  ]
            }
          />
        </>
      ) : (
        <>
          <Line />

          {readOnly ? (
            <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
          ) : null}

          <Row mb={1}>
            <SearchBox>
              <TextField
                label={t('searchUser')}
                value={searchTerm}
                fullWidth
                variant="outlined"
                size="medium"
                onChange={(event: React.ChangeEvent<{ value: string }>) => {
                  setSearchTerm(event.target.value);
                }}
                InputProps={{
                  endAdornment: loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SearchIcon color="action" />
                  )
                }}
              />
            </SearchBox>
            <Gap />
            {canAdministerUsers && (
              <Button
                color="primary"
                startIcon={<AddIcon />}
                aria-label="add"
                onClick={onAdd}
                disabled={readOnly}
              >
                {t('addUser')}
              </Button>
            )}
            {canAdministerUsers && global?.batchImport && (
              <Button
                color="secondary"
                startIcon={<ImportIcon />}
                aria-label="import"
                onClick={onImport}
                disabled={readOnly}
              >
                {t('importUsers')}
              </Button>
            )}
          </Row>
          <Table
            maxHeight="600px"
            headers={[
              t('email'),
              t('firstName'),
              t('lastName'),
              t('dob'),
              t('title'),
              t('manager'),
              ''
            ]}
            rows={filteredUsers.map(user => ({
              id: user.id,
              cells: [
                user.email,
                user.firstName,
                user.lastName ?? '',
                user.dob ?? '',
                user.title ?? '',
                user.managerId
                  ? `${data?.searchUsers?.[user.managerId]?.firstName ??
                      ''} ${data?.searchUsers?.[user.managerId]?.lastName ??
                      ''}`
                  : '',
                <Row key="icons">
                  <Spacer />
                  {canAdministerUsers && (
                    <IconButton
                      onClick={() => onDelete(user.id)}
                      title={t('delete')}
                      disabled={readOnly}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {canAdministerUsers && (
                    <FileUploadButton
                      organization={organization}
                      id={user.id}
                      disabled={readOnly}
                    />
                  )}
                  <IconButton
                    onClick={() => selectUser(user.id)}
                    title={t('viewUser')}
                  >
                    <GoToUserIcon />
                  </IconButton>
                  <Spacer />
                </Row>
              ]
            }))}
          />
          <EditUserDialog
            organization={organization}
            title={t('userDialogTitle')}
            text={t('userDialogSubtitle')}
            initialValue={{
              firstName: editDialog?.firstName,
              lastName: editDialog?.lastName,
              dob: editDialog?.dob,
              email: editDialog?.email,
              startDate: editDialog?.startDate,
              managerId: editDialog?.managerId
            }}
            requiredOnboardingDocuments={
              editDialog?.requiredOnboardingDocuments
            }
            dialogOpen={canAdministerUsers && !!editDialog}
            onClose={() => setEditDialog(null)}
            onSubmit={editDialog?.callback}
          />
          <ConfirmDialog
            title={confirmDialog?.title ?? ''}
            text={confirmDialog?.text ?? ''}
            dialogOpen={!!confirmDialog}
            onClose={() => setConfirmDialog(null)}
            onSubmit={confirmDialog?.callback}
          />
          <ImportUsersDialog
            dialogOpen={canAdministerUsers && !!importUsersDialog}
            onClose={() => setImportUsersDialog(null)}
            onSubmit={importUsersDialog?.callback}
          />
        </>
      )}
    </StaffTabs>
  );
}
